import { AtlasObjectType, IAtlasObjectRef } from '../helpers/atlasUrls'
import { ITaskLinkPreview } from './task'

/** The rendered-links-per-body budget one request answers. */
export const ATLAS_OBJECT_PREVIEW_BATCH_SIZE = 50

/**
 * What a rendered Atlas link shows instead of its address.
 *
 * Three outcomes, and the chip renders each differently, so they must stay
 * distinguishable:
 *
 * - resolved — `restricted: false` with a name. Shows the record.
 * - restricted — the reader's role can't open this kind of record. Shows
 *   `<restricted link>`; no name is sent.
 * - absent from the response — unknown, deleted or malformed id. The chip falls
 *   back to the raw URL, exactly as an unresolvable task link does today.
 */
export type IAtlasObjectPreview =
  | (IAtlasObjectRef & {
      restricted: false
      name: string
      /** Client logo where one exists. Always null for a person — the CRM has no photo column yet. */
      imageUrl: string | null
      /**
       * The source ids a task chip picks its status glyph from. Present only for
       * a task: every other record is named and pictured, a task is named and
       * given a state.
       */
      task?: ITaskLinkPreview
    })
  | (IAtlasObjectRef & { restricted: true })

export const RESTRICTED_LINK_LABEL = '<restricted link>'

/** Ids are only unique within their own table, so a preview is keyed by both. */
export const atlasObjectRefKey = ({ type, id }: IAtlasObjectRef): string => `${type}:${id}`

export interface IListAtlasObjectPreviewsParams {
  refs: IAtlasObjectRef[]
}

/** Narrowing helper — `restricted` is the discriminant, but reads poorly inline. */
export const isResolvedPreview = (
  preview: IAtlasObjectPreview,
): preview is Extract<IAtlasObjectPreview, { restricted: false }> => !preview.restricted

export const isPreviewableType = (value: string): value is AtlasObjectType =>
  Object.values(AtlasObjectType).includes(value as AtlasObjectType)

export enum AtlasObjectDetailFormat {
  Text = 'text',
  LongText = 'longText',
  Date = 'date',
  Currency = 'currency',
  /** A ratio stored as a decimal (0.42), shown as a percentage. */
  Percent = 'percent',
  Email = 'email',
  Phone = 'phone',
  Url = 'url',
}

export interface IAtlasObjectDetailField {
  label: string
  value: string | number
  format: AtlasObjectDetailFormat
}

export interface IAtlasObjectDetail extends IAtlasObjectRef {
  restricted: false
  name: string
  imageUrl: string | null
  fields: IAtlasObjectDetailField[]
  /**
   * Set when the link names another identity of the record shown: a CRM company that is
   * now a client is described, and opened, as that client — as its own page redirects.
   */
  url?: string
  /** A teammate the reader can start a direct message with: active, and not the reader. */
  canMessage?: boolean
}

/** Same contract as a chip: a record the reader may not open answers `restricted`, never a 403. */
export type IAtlasObjectDetailResponse =
  IAtlasObjectDetail | (IAtlasObjectRef & { restricted: true })

export const isResolvedDetail = (
  detail: IAtlasObjectDetailResponse,
): detail is IAtlasObjectDetail => !detail.restricted

export type IGetAtlasObjectDetailParams = IAtlasObjectRef
